import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { catchError, forkJoin, map, mergeMap, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ChatService } from '../../pages/chat/services/chat.service';
import { UserConfig, UserProfile } from '../models/auth.model';

const AUTH_URL = environment.authUrl;
const IS_PRODUCTION = environment.prod;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private ngxPermissions = inject(NgxPermissionsService);
  private router = inject(Router);
  private chatService = inject(ChatService);

  private _isInitialized = signal<boolean>(false);
  isInitialized = this._isInitialized.asReadonly();

  private _isAuthenticated = signal<boolean>(false);
  isAuthenticated = this._isAuthenticated.asReadonly();

  private _accessToken = signal<string | null>(null);
  accessToken = this._accessToken.asReadonly();

  private _userId = signal<number | null>(null);
  userId = this._userId.asReadonly();

  private _userProfile = signal<UserProfile | null>(null);
  userProfile = this._userProfile.asReadonly();

  private _userConfig = signal<UserConfig | null>(null);
  userConfig = this._userConfig.asReadonly();

  renewTimer: any;

  init() {
    const accessToken = this.getCookie('access_token');
    const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');

    if (accessToken && this.getTokenExpiration(accessToken) > new Date()) {
      this._accessToken.set(accessToken);
      this._isAuthenticated.set(true);

      this.autoRenewToken();

      return of(true).pipe(
        mergeMap(() => forkJoin([this.getMyConfig(), this.getMyProfile()])),
        tap(() => this._isInitialized.set(true)),
        mergeMap(() => of(true))
      );
    } else if (refreshToken && this.getTokenExpiration(refreshToken) > new Date()) {
      return this.refreshToken().pipe(
        tap(() => this._isAuthenticated.set(true)),
        mergeMap(() => forkJoin([this.getMyConfig(), this.getMyProfile()])),
        tap(() => this._isInitialized.set(true)),
        mergeMap(() => of(true))
      );
    } else {
      return this.logout().pipe(tap(() => this._isInitialized.set(true)));
    }
  }

  login(opts: { email: string; password: string; remember?: boolean; }) {
    const { email, password, remember } = opts;

    return this.http
      .post<{ token: string; exp: Date; }>(`${AUTH_URL}/login`, {
        email,
        password,
        platform: 'AI',
      })
      .pipe(
        tap(async response => {
          if (remember) {
            localStorage.setItem('refreshToken', response.token);
          } else {
            localStorage.removeItem('refreshToken');
          }

          sessionStorage.setItem('refreshToken', response.token);
          const duration = new Date(response.exp).getTime() - new Date().getTime();
          this.setCookie('access_token', response.token, duration);

          this.autoRenewToken();

          this._accessToken.set(response.token);
          this._isAuthenticated.set(true);
        }),
        mergeMap(() => forkJoin([this.getMyConfig(), this.getMyProfile()])),
        catchError(_ => {
          this.logout();
          return [];
        })
      );
  }

  logout(redirect = true) {
    this.deleteCookie('access_token');
    sessionStorage.clear();
    localStorage.clear();
    this._isAuthenticated.set(false);
    this._accessToken.set(null);
    this._userId.set(null);
    this._userProfile.set(null);

    if (this.renewTimer) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }

    this.ngxPermissions.flushPermissions();

    this.chatService.clearChat();
    this.chatService.clearContext();

    if (redirect) {
      const currentUrl = new URL(window.location.href);

      if (currentUrl.pathname.startsWith('/auth')) {
        return of(false);
      }

      if (currentUrl.searchParams.has('redirectUrl')) {
        this.router.navigate(['/auth/login'], { queryParams: { redirectUrl: currentUrl.searchParams.get('redirectUrl') } });
        return of(false);
      }

      const redirectUrl = currentUrl.pathname + currentUrl.search + currentUrl.hash;
      this.router.navigate(['/auth/login'], { queryParams: { redirectUrl } });
      return of(false);
    }
    return of(false);
  }

  refreshToken() {
    const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');

    if (!(refreshToken && this.getTokenExpiration(refreshToken) > new Date())) {
      return this.logout();
    }

    return this.http
      .post<{ token: string; exp: Date; }>(
        `${AUTH_URL}/renew`,
        {
          platform: 'AI',
        },
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            ignoreError: 'true',
            ignoreSpinner: 'true',
          },
        }
      )
      .pipe(
        tap(response => {
          const duration = new Date(response.exp).getTime() - new Date().getTime();
          this.setCookie('access_token', response.token, duration);
          this._accessToken.set(response.token);
          this._isAuthenticated.set(true);

          this.autoRenewToken();
        }),
        map(() => true),
        catchError(error => {
          if (error.status !== 500) this.logout();
          return [];
        })
      );
  }

  getMyConfig() {
    return this.http.get<UserConfig>(`${AUTH_URL}/config`).pipe(
      tap(config => {
        this._userConfig.set(config);
        this.ngxPermissions.loadPermissions(['any', ...config.permissions]);
      })
    );
  }

  getMyProfile() {
    return this.http.get<UserProfile>(`${AUTH_URL}/profile`).pipe(tap(profile => this._userProfile.set(profile)));
  }

  hasRole(roles: number | number[]) {
    if (Array.isArray(roles)) {
      return !!roles.filter(role => role === this.userConfig?.()?.role.code).length;
    } else {
      return roles === this.userConfig()?.role.code;
    }
  }

  private autoRenewToken() {
    if (this.renewTimer) {
      clearTimeout(this.renewTimer);
    }

    const token = this.accessToken();
    if (!token) return;

    const expiration = this.getTokenExpiration(token);
    const diff = expiration.getTime() - new Date().getTime();

    if (diff > 0) {
      this.renewTimer = setTimeout(
        () => {
          this.refreshToken().subscribe();
        },
        diff - 1000 * 60 * 5
      );
    }
  }

  private getTokenExpiration(token: string) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      return new Date(decoded.exp * 1000);
    } catch (error) { }

    return new Date(0);
  }

  private setCookie(name: string, value: string, duration?: number) {
    const isHttps = window.location.protocol === 'https:';
    const domain = IS_PRODUCTION ? ';domain=.digitaloilfield.app' : '';

    let sameSite = ';SameSite=Lax';
    let secure = '';
    let expires = '';

    if (duration) {
      const date = new Date();
      date.setTime(date.getTime() + duration);
      expires = '; expires=' + date.toUTCString();
    }

    if (isHttps) {
      sameSite = ';SameSite=None';
      secure = ';Secure';
    }

    document.cookie = name + '=' + (value || '') + expires + ';path=/' + domain + sameSite + secure;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private deleteCookie(name: string) {
    document.cookie = name + '=; Max-Age=-99999999;';
  }
}
